<template>
  <div class="v2-inventory">
    <h3 class="v2-inventory__section-title">
      Inventory &amp; MOQ
    </h3>

    <!-- Top row: Min Qty Per Style, Min Qty Per Size, Is Pack -->
    <div class="v2-inventory__top-row">
      <div class="v2-inventory__field">
        <label class="v2-inventory__label">Min Qty Per Style</label>
        <validation-provider
          #default="{ errors }"
          name="Min Qty Per Style"
          rules="numeric"
        >
          <input
            v-model="minQtyPerStyle"
            type="number"
            class="v2-inventory__input"
            :class="{ 'v2-inventory__input--error': errors[0] }"
            placeholder="Enter Min Qty Per Style"
            @keypress="onlyAllowPositiveDecimal"
            @paste="blockInvalidPaste"
          >
          <span
            v-if="errors[0]"
            class="v2-inventory__error"
          >{{ errors[0] }}</span>
        </validation-provider>
      </div>
      <div class="v2-inventory__field">
        <label class="v2-inventory__label">Min Qty Per Size</label>
        <validation-provider
          #default="{ errors }"
          name="Min Qty Per Size"
          rules="numeric"
        >
          <input
            v-model="minQtyPerSize"
            type="number"
            class="v2-inventory__input"
            :class="{ 'v2-inventory__input--error': errors[0] }"
            placeholder="Enter Min Qty Per Size"
            @keypress="onlyAllowPositiveDecimal"
            @paste="blockInvalidPaste"
          >
          <span
            v-if="errors[0]"
            class="v2-inventory__error"
          >{{ errors[0] }}</span>
        </validation-provider>
      </div>
      <div class="v2-inventory__field v2-inventory__field--checkbox">
        <label class="v2-inventory__checkbox-wrap">
          <input
            v-model="isCarton"
            type="checkbox"
            class="v2-inventory__checkbox-input"
          >
          <span class="v2-inventory__checkbox-box">
            <svg
              v-if="isCarton"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </span>
          <span class="v2-inventory__checkbox-label">It's a pack</span>
        </label>
      </div>
    </div>

    <!-- Single size + single color variant -->
    <div
      v-if="isSingleColorSingleSize"
      class="v2-inventory__single"
    >
      <v2-color-picker
        :value="{ color: singleItem.color, colorFamily: singleItem.colorFamily }"
        :item-index="0"
      />
      <div class="v2-inventory__single-row">
        <div class="v2-inventory__field">
          <label class="v2-inventory__label">Size</label>
          <input
            :value="singleItem.size"
            class="v2-inventory__input v2-inventory__input--disabled"
            disabled
          >
        </div>
        <div class="v2-inventory__field">
          <label class="v2-inventory__label">Inventory</label>
          <input
            :value="singleItem.quantityAvailable"
            class="v2-inventory__input v2-inventory__input--disabled"
            disabled
            type="number"
          >
        </div>
        <div
          v-if="isCarton"
          class="v2-inventory__field"
        >
          <label class="v2-inventory__label">
            Pack Qty
            <span class="v2-inventory__required">*</span>
          </label>
          <validation-provider
            #default="{ errors }"
            name="Pack Qty"
            :rules="isCarton ? 'required' : ''"
          >
            <input
              v-model="singleItem.value"
              type="number"
              min="0"
              class="v2-inventory__input"
              :class="{ 'v2-inventory__input--error': errors[0] }"
              placeholder="Enter Pack Qty"
              @input="handleSingleSizeChange({ value: $event.target.value })"
            >
            <span
              v-if="errors[0]"
              class="v2-inventory__error"
            >{{ errors[0] }}</span>
          </validation-provider>
        </div>
      </div>
    </div>

    <!-- Multi-variant grid -->
    <div v-else>
      <div
        v-for="(variant, variantIndex) in variants"
        :key="`variant-${variantIndex}`"
        class="v2-inventory__variant"
        :class="{'v2-inventory__variant--bordered': variants.length > 1}"
      >
        <div
          v-if="variants.length > 1"
          class="v2-inventory__variant-header"
        >
          Variant {{ variantIndex + 1 }}
        </div>

        <v2-color-picker
          :value="{ color: variant.color, colorFamily: variant.colorFamily }"
          @input="updated => handleVariantChange(updated, variantIndex)"
        />

        <!-- Sizes grid table -->
        <div class="v2-inventory__grid">
          <div class="v2-inventory__grid-scroll">
            <table class="v2-inventory__table">
              <thead>
                <tr>
                  <th class="v2-inventory__row-label">
                    Size
                  </th>
                  <th
                    v-for="(size, sizeIndex) in sizeOptions"
                    :key="`size-header-${sizeIndex}`"
                    class="v2-inventory__cell"
                  >
                    {{ size }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <!-- Inventory row (disabled) -->
                <tr>
                  <td class="v2-inventory__row-label">
                    Inventory
                  </td>
                  <td
                    v-for="(size, sizeIndex) in sizeOptions"
                    :key="`inv-${sizeIndex}`"
                    class="v2-inventory__cell"
                  >
                    <input
                      :value="variant.inventory[size]"
                      type="number"
                      class="v2-inventory__cell-input v2-inventory__cell-input--disabled"
                      disabled
                    >
                  </td>
                </tr>
                <!-- Pack Qty row (editable, only when isCarton) -->
                <tr v-if="isCarton">
                  <td class="v2-inventory__row-label">
                    Pack Qty <span class="v2-inventory__required">*</span>
                  </td>
                  <td
                    v-for="(size, sizeIndex) in sizeOptions"
                    :key="`pack-${sizeIndex}`"
                    class="v2-inventory__cell"
                  >
                    <validation-provider
                      #default="{ errors }"
                      :name="`Pack Qty ${variantIndex}-${size}`"
                      :rules="isCarton ? 'required' : ''"
                    >
                      <input
                        v-model="variant.packQty[size]"
                        type="number"
                        min="0"
                        class="v2-inventory__cell-input"
                        :class="{ 'v2-inventory__cell-input--error': errors[0] }"
                        @input="(e) => handleVariantChange({
                          color: variant.color,
                          colorFamily: variant.colorFamily,
                          newVal: e.target.value,
                          rowKey: 'packQty',
                          size: size,
                        }, variantIndex)"
                      >
                    </validation-provider>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useInventory } from '@/views/apps/product/edit/input-sections/hooks/useInventory'
import { ValidationProvider } from 'vee-validate'
import { blockInvalidPaste, onlyAllowPositiveDecimal } from '@/views/apps/product/edit/utils'
import V2ColorPicker from './V2ColorPicker.vue'

export default {
  name: 'V2ProductInventory',
  components: {
    ValidationProvider,
    V2ColorPicker,
  },
  data() {
    return {
      sizeOptions: [],
      variants: [],
    }
  },
  setup() {
    const {
      isCarton,
      items,
      minQtyPerSize,
      minQtyPerStyle,
    } = useInventory()
    return {
      isCarton,
      items,
      minQtyPerSize,
      minQtyPerStyle,
    }
  },
  computed: {
    isSingleColorSingleSize() {
      return this.items && this.items.length === 1
    },
    singleItem() {
      if (!this.items || this.items.length === 0) return {}
      const item = this.items[0]
      return {
        ...item,
        minQtyPerSize: item.minQtyPerSize ?? this.minQtyPerSize ?? 0,
        value: item.value ?? 0,
        quantityAvailable: item.quantityAvailable ?? 0,
      }
    },
  },
  watch: {
    items: {
      immediate: true,
      handler(newItems) {
        if (this._skipNextItemsRebuild) {
          this._skipNextItemsRebuild = false
          return
        }
        this.buildVariants(newItems)
      },
    },
  },
  created() {
    // Non-reactive guard: when buildItems writes back to items as part of
    // handleVariantChange, the items watcher would otherwise rebuild
    // `variants` from the new items via getGroupedItems — which keys on
    // `${colorFamily}-${color}` and collapses two variants into one when
    // both colors are cleared in the same colorFamily, silently dropping
    // per-variant SKUs / packQty. Skip the rebuild for our own writes;
    // external item changes still trigger it.
    this._skipNextItemsRebuild = false
  },
  methods: {
    blockInvalidPaste,
    onlyAllowPositiveDecimal,
    handleSingleSizeChange(newValues) {
      if ('value' in newValues) {
        const value = newValues.value ? Number(newValues.value) : newValues.value
        this.items = [
          {
            ...this.items[0],
            value,
          },
        ]
      }
    },
    _buildSizeUpdate(rowKey, variant, size, newVal) {
      const hasSizeUpdate = size && newVal !== undefined
      if (!hasSizeUpdate) return variant[rowKey]
      return { ...variant[rowKey], [size]: newVal }
    },
    _buildUpdatedVariant(variant, updateData) {
      const { rowKey, color, colorFamily } = updateData
      // Use ?? not || here: an explicitly cleared color ('') must be
      // preserved, otherwise the fallback restores the prior value and the
      // input "snaps back" on blur.
      const updated = {
        ...variant,
        color: color ?? variant.color,
        colorFamily: colorFamily ?? variant.colorFamily,
      }
      if (rowKey) {
        updated[rowKey] = this._buildSizeUpdate(rowKey, variant, updateData.size, updateData.newVal)
      }
      return updated
    },
    _replaceVariantAtIndex(variantIndex, updateData) {
      return this.variants.map((variant, index) => (
        index === variantIndex
          ? this._buildUpdatedVariant(variant, updateData)
          : variant
      ))
    },
    handleVariantChange(updateData, variantIndex) {
      this.variants = this._replaceVariantAtIndex(variantIndex, updateData)
      this.buildItems(this.variants)
    },
    getGroupedItems(items) {
      if (!items) return {}
      const groups = {}
      const sizeOptions = []
      items.forEach(item => {
        if (!sizeOptions.includes(item.size)) sizeOptions.push(item.size)
        const key = `${item.colorFamily || ''}-${item.color || ''}`
        if (!groups[key]) {
          groups[key] = {
            colorFamily: item.colorFamily,
            color: item.color,
            items: {},
          }
        }
        groups[key].items[item.size] = {
          inventory: item.quantityAvailable,
          packQty: item.value,
          minQtyPerSize: item.minQtyPerSize,
          sku: item.sku,
        }
      })
      this.sizeOptions = sizeOptions
      return groups
    },
    buildItems(variants) {
      const newItems = []
      this.items.forEach(item => {
        const variant = variants.find(v => v.skuIds.includes(item.sku))
        if (!variant) return

        const value = variant.packQty[item.size]
        const colorFamily = variant.colorFamily
        const color = variant.color

        newItems.push({
          ...item,
          value,
          colorFamily,
          color,
        })
      })
      this._skipNextItemsRebuild = true
      this.items = newItems
    },
    buildVariants(items) {
      const groupedItems = this.getGroupedItems(items)
      if (Object.keys(groupedItems).length === 0) return
      this.variants = Object.values(groupedItems).map(variant => {
        const inventoryObj = {}
        const packQtyObj = {}
        const minQtyPerSizeObj = {}
        const skuIds = []

        this.sizeOptions.forEach(size => {
          const sizeData = variant.items[size] || {}
          inventoryObj[size] = sizeData.inventory || 0
          packQtyObj[size] = sizeData.packQty ?? null
          minQtyPerSizeObj[size] = sizeData.minQtyPerSize || null
          skuIds.push(sizeData.sku)
        })
        return {
          colorFamily: variant.colorFamily,
          color: variant.color,
          inventory: inventoryObj,
          packQty: packQtyObj,
          minQtyPerSize: minQtyPerSizeObj,
          skuIds,
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-inventory {
  margin-top: var(--v2-space-6);

  &__section-title {
    font-family: var(--v2-font-display);
    font-size: 1rem;
    font-weight: 600;
    color: var(--v2-foreground);
    margin-bottom: var(--v2-space-4);
    padding-bottom: var(--v2-space-2);
    border-bottom: 1px solid var(--v2-border);
  }

  &__top-row {
    display: flex;
    gap: var(--v2-space-4);
    align-items: flex-end;
    margin-bottom: var(--v2-space-4);
  }

  &__field {
    flex: 1;
    min-width: 0;

    &--checkbox {
      display: flex;
      align-items: center;
      padding-bottom: var(--v2-space-2);
    }
  }

  &__label {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--v2-muted-foreground);
    margin-bottom: var(--v2-space-1);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  &__required {
    color: var(--v2-destructive);
  }

  // Glass form field — matches V2Input recipe.
  &__input {
    width: 100%;
    padding: var(--v2-space-2) var(--v2-space-3);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-md);
    font-size: 0.875rem;
    font-family: var(--v2-font-sans);
    color: var(--v2-foreground);
    background: var(--v2-glass-surface-2);
    min-height: 38px;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover:not(:disabled):not(:focus):not([readonly]) {
      border-color: var(--v2-glass-stroke-strong);
    }

    &:focus {
      outline: none;
      border-color: var(--v2-primary);
      box-shadow: 0 0 0 3px oklch(0.62 0.21 295 / 0.25);
    }

    &--disabled {
      background: var(--v2-muted);
      backdrop-filter: none;
      cursor: not-allowed;
    }

    &--error {
      border-color: var(--v2-destructive);
    }
  }

  &__error {
    display: block;
    font-size: 0.6875rem;
    color: var(--v2-destructive);
    margin-top: var(--v2-space-1);
  }

  &__checkbox-wrap {
    display: inline-flex;
    align-items: center;
    gap: var(--v2-space-2);
    cursor: pointer;
    user-select: none;
  }

  &__checkbox-input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  &__checkbox-box {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1rem;
    height: 1rem;
    border: 1.5px solid var(--v2-border);
    border-radius: var(--v2-radius-sm);
    background: var(--v2-background);
    color: var(--v2-primary-foreground);
    transition: all 0.15s ease;
    flex-shrink: 0;
  }

  &__checkbox-input:checked + &__checkbox-box {
    background: var(--v2-primary);
    border-color: var(--v2-primary);
  }

  &__checkbox-label {
    font-size: 0.8125rem;
    color: var(--v2-foreground);
  }

  &__single {
    margin-top: var(--v2-space-3);
  }

  &__single-row {
    display: flex;
    gap: var(--v2-space-4);
    margin-top: var(--v2-space-2);
  }

  &__variant {
    margin-top: var(--v2-space-4);

    &--bordered {
      padding: var(--v2-space-4);
      border: 1px solid var(--v2-border);
      border-radius: var(--v2-radius-md);
      margin-bottom: var(--v2-space-3);
    }
  }

  &__variant-header {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--v2-foreground);
    margin-bottom: var(--v2-space-2);
  }

  &__grid {
    margin-top: var(--v2-space-4);
  }

  &__grid-scroll {
    overflow-x: auto;
  }

  &__row-label {
    font-size: 0.6875rem;
    font-weight: 600;
    color: var(--v2-muted-foreground);
    text-transform: uppercase;
    white-space: nowrap;
    padding-right: var(--v2-space-3) !important;
    text-align: left !important;
    vertical-align: middle;
  }

  &__table {
    border-collapse: separate;
    border-spacing: var(--v2-space-2);

    th, td {
      padding: 0;
    }

    th {
      font-size: 0.6875rem;
      font-weight: 600;
      color: var(--v2-muted-foreground);
      text-align: center;
      padding-bottom: 0;
      text-transform: uppercase;
    }
  }

  &__cell {
    padding: 2px;
    min-width: 60px;
  }

  // Glass cell input — narrow inline input for inventory grid cells.
  &__cell-input {
    width: 100%;
    min-width: 50px;
    padding: var(--v2-space-1) var(--v2-space-2);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-sm);
    font-size: 0.75rem;
    font-family: var(--v2-font-sans);
    color: var(--v2-foreground);
    background: var(--v2-glass-surface-2);
    text-align: center;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:focus {
      outline: none;
      border-color: var(--v2-primary);
    }

    &--disabled {
      background: var(--v2-muted);
      backdrop-filter: none;
      cursor: not-allowed;
      color: var(--v2-muted-foreground);
    }

    &--error {
      border-color: var(--v2-destructive);
    }
  }
}
</style>
